import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Heart, ChevronDown } from "lucide-react";
import { useAuth, loginWithGoogle } from "../lib/auth";

const NAV = [
  { to: "/about", label: "About" },
  {
    label: "Programs",
    to: "/programs",
    children: [
      { to: "/programs", label: "All Programs" },
      { to: "/strong-minds", label: "Change Choices: Strong Minds" },
      { to: "/little-leaders", label: "Little Leaders (Ages 5–9)" },
      { to: "/turnaround", label: "Turnaround Toolkit" },
    ],
  },
  { to: "/strong-minds-center", label: "Strong Minds" },
  { to: "/parents", label: "Parents" },
  {
    label: "Get Involved",
    to: "/get-involved",
    children: [
      { to: "/become-a-mentee", label: "Enroll Your Child" },
      { to: "/become-a-mentor", label: "Become a Mentor" },
      { to: "/volunteer", label: "Volunteer" },
      { to: "/partnerships", label: "Partner With Us" },
      { to: "/kings-circle", label: "Kings' Circle" },
      { to: "/new-home", label: "Stand Behind Our New Home" },
    ],
  },
  { to: "/impact", label: "Our Impact" },
  { to: "/events", label: "Events" },
  { to: "/contact", label: "Contact" },
];

function DesktopItem({ item }) {
  const [open, setOpen] = useState(false);
  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        data-testid={`nav-${item.to.replace("/", "")}`}
        className={({ isActive }) =>
          `text-sm font-medium transition-colors ${isActive ? "text-[#6B21A8]" : "text-black/80 hover:text-[#6B21A8]"}`
        }
      >
        {item.label}
      </NavLink>
    );
  }
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        className="flex items-center gap-1 text-sm font-medium text-black/80 hover:text-[#6B21A8]"
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {item.label} <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-2 min-w-[260px]">
          <div className="rounded-lg border border-black/10 bg-white p-2 shadow-xl">
            {item.children.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={`nav-sub-${c.to.replace("/", "")}`}
                className="block rounded-md px-3 py-2 text-sm text-black/80 hover:bg-[#6B21A8]/5 hover:text-[#6B21A8]"
              >
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <header className="sticky top-0 z-40 border-b border-black/10 bg-white/85 backdrop-blur-xl" data-testid="site-header">
      <div className="container-x flex items-center justify-between py-4">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
          <img
            src="/logo-dark.png"
            alt="The Change Center Mentoring Group"
            className="h-14 w-auto md:h-16"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) => <DesktopItem key={n.label} item={n} />)}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <NavLink to="/portal" data-testid="nav-portal" className={({ isActive }) => `text-sm font-medium ${isActive ? "text-[#6B21A8]" : "text-black/80 hover:text-[#6B21A8]"}`}>Portal</NavLink>
              {user.role === "admin" && (
                <NavLink to="/admin" data-testid="nav-admin" className={({ isActive }) => `text-sm font-medium ${isActive ? "text-[#6B21A8]" : "text-black/80 hover:text-[#6B21A8]"}`}>Admin</NavLink>
              )}
              <span className="text-sm text-black/70" data-testid="user-name">Hi, {user.name.split(" ")[0]}</span>
              <button onClick={logout} data-testid="logout-btn" className="text-sm text-black/60 hover:text-black">Sign out</button>
            </>
          ) : (
            <button onClick={loginWithGoogle} data-testid="sign-in-btn" className="text-sm font-medium text-black/70 hover:text-[#6B21A8]">
              Sign in
            </button>
          )}
          <Link to="/donate" data-testid="donate-header-btn" className="inline-flex items-center gap-2 rounded-md bg-[#F5B90A] px-5 py-2.5 font-semibold text-black transition-transform hover:-translate-y-0.5 hover:bg-[#FFC93A]">
            <Heart className="h-4 w-4" strokeWidth={2.4} /> Donate
          </Link>
        </div>

        <button className="lg:hidden" data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} aria-label="Toggle menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/10 bg-white max-h-[75vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="container-x flex flex-col gap-1 py-4">
            {NAV.map((n) => (
              n.children ? (
                <div key={n.label} className="py-1">
                  <button
                    className="flex w-full items-center justify-between py-2 text-sm font-medium text-black/80"
                    onClick={() => setExpanded((p) => ({ ...p, [n.label]: !p[n.label] }))}
                    data-testid={`mobile-nav-group-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {n.label}
                    <ChevronDown className={`h-4 w-4 transition-transform ${expanded[n.label] ? "rotate-180" : ""}`} />
                  </button>
                  {expanded[n.label] && (
                    <div className="ml-3 border-l border-black/10 pl-3">
                      {n.children.map((c) => (
                        <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="block py-2 text-sm text-black/70 hover:text-[#6B21A8]" data-testid={`mobile-nav-${c.to.replace("/", "")}`}>
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className={`py-2 text-sm font-medium ${location.pathname === n.to ? "text-[#6B21A8]" : "text-black/80"}`} data-testid={`mobile-nav-${n.to.replace("/", "")}`}>
                  {n.label}
                </Link>
              )
            ))}
            <Link to="/donate" onClick={() => setOpen(false)} data-testid="mobile-donate-btn" className="btn-gold mt-3 w-full">Donate</Link>
            {user ? (
              <>
                <Link to="/portal" onClick={() => setOpen(false)} className="py-2 text-sm font-medium text-black/80">Portal</Link>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="py-2 text-sm font-medium text-black/80">Admin</Link>
                )}
                <button onClick={() => { setOpen(false); logout(); }} className="py-2 text-left text-sm text-black/60">Sign out</button>
              </>
            ) : (
              <button onClick={loginWithGoogle} className="btn-outline w-full mt-2" data-testid="mobile-sign-in-btn">Sign in with Google</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="site-footer">
      <div className="noise" />
      <div className="container-x py-20 md:py-28">
        <div className="grid gap-14 md:grid-cols-12">
          <div className="md:col-span-5">
            <img src="/logo.png" alt="The Change Center Mentoring Group" className="mb-8 h-auto w-56" />
            <span className="gold-line mb-6" />
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
              Changing this generation<br />
              <span className="text-[#F5B90A] italic">one young man</span> at a time.
            </h2>
            <p className="mt-6 max-w-md text-white/70">
              A 501(c)(3) nonprofit serving boys ages 10–17 through mentorship, education, and life skills in Clayton County and beyond.
            </p>
          </div>

          <div className="md:col-span-2">
            <p className="overline text-[#F5B90A] mb-4">Explore</p>
            <ul className="space-y-2 text-white/80">
              <li><Link to="/about" className="hover:text-[#F5B90A]">About</Link></li>
              <li><Link to="/programs" className="hover:text-[#F5B90A]">Programs</Link></li>
              <li><Link to="/strong-minds" className="hover:text-[#F5B90A]">Strong Minds</Link></li>
              <li><Link to="/parents" className="hover:text-[#F5B90A]">Parents</Link></li>
              <li><Link to="/impact" className="hover:text-[#F5B90A]">Our Impact</Link></li>
              <li><Link to="/events" className="hover:text-[#F5B90A]">Events</Link></li>
              <li><Link to="/blog" className="hover:text-[#F5B90A]">News</Link></li>
              <li><Link to="/resources" className="hover:text-[#F5B90A]">Resources</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="overline text-[#F5B90A] mb-4">Get Involved</p>
            <ul className="space-y-2 text-white/80">
              <li><Link to="/enroll" className="hover:text-[#F5B90A]">Enroll Your Child</Link></li>
              <li><Link to="/become-a-mentor" className="hover:text-[#F5B90A]">Become a Mentor</Link></li>
              <li><Link to="/volunteer" className="hover:text-[#F5B90A]">Volunteer</Link></li>
              <li><Link to="/partnerships" className="hover:text-[#F5B90A]">Partner With Us</Link></li>
              <li><Link to="/kings-circle" className="hover:text-[#F5B90A]">Kings' Circle</Link></li>
              <li><Link to="/donate" className="hover:text-[#F5B90A]">Donate</Link></li>
              <li><Link to="/turnaround" className="hover:text-[#F5B90A]">Turnaround Toolkit</Link></li>
              <li><Link to="/request-help" className="hover:text-[#F5B90A]">Request Help</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="overline text-[#F5B90A] mb-4">Contact</p>
            <address className="not-italic space-y-2 text-white/80">
              <p>Clayton County, Georgia</p>
              <p><a href="mailto:change@changecentermentoring.com" className="hover:text-[#F5B90A]">change@changecentermentoring.com</a></p>
              <p><a href="tel:+14043260944" className="hover:text-[#F5B90A]">(404) 326-0944</a></p>
            </address>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-white/50">© {new Date().getFullYear()} The Change Center Mentoring Group. A 501(c)(3) nonprofit. EIN 99-4290337.</p>
          <p className="overline text-white/60">Built with brotherhood.</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
